// packages/ui/src/components/LightTooltipBase.tsx
'use client';

/**
 * `LightTooltip` without MUI: the same tooltip, given the theme it reads its look from (`theme`) instead of taking it
 * from MUI's context, for a bundle with no MUI at all (the terms widget, which gives it a stand-in for MUI's default
 * theme with only the parts read here). `LightTooltip` is this with MUI's theme; its doc says what the tooltip does.
 */
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type Ref,
  type SyntheticEvent,
  type TouchEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { placeLightTooltip, toDevicePixels } from './lightTooltipPlacement';
import { isShadowRoot } from '../surface/composedTree';
import { useWidgetSurface } from '../surface/WidgetSurface';

/** MUI Tooltip's defaults (`enterDelay`, `enterTouchDelay`, `leaveTouchDelay`) and its 800 ms hysteresis. */
const ENTER_DELAY_MS = 100;
const ENTER_TOUCH_DELAY_MS = 700;
const LEAVE_TOUCH_DELAY_MS = 1500;
const HYSTERESIS_MS = 800;

/** MUI Grow's start: `scale(0.75, 0.75²)`, transparent. */
const GROW_FROM = 'scale(0.75, 0.5625)';

/** Shared by every LightTooltip, as MUI's is by every MUI Tooltip: right after one closes, the next opens at once. */
let hysteresisOpen = false;
let hysteresisTimer: ReturnType<typeof setTimeout> | undefined;

/** The page's own text selection, which MUI turns off while a touch is held (iOS selects text on a long press). */
const USER_SELECT = '-webkit-user-select';
const bodyUserSelect = () => document.body.style.getPropertyValue(USER_SELECT);
const setBodyUserSelect = (value: string) => document.body.style.setProperty(USER_SELECT, value);

/** The parts of MUI's theme the tooltip reads (MUI's `Theme` is one). */
export interface LightTooltipTheme {
  zIndex: { tooltip: number };
  palette: { grey: { 700: string }; common: { white: string } };
  shape: { borderRadius: number | string };
  typography: {
    fontFamily?: string;
    fontWeightMedium?: number | string;
    fontWeightRegular?: number | string;
    pxToRem: (px: number) => string;
  };
  transitions: {
    duration: { shorter: number; shortest: number };
    create: (property: string, options: { duration: number; delay?: number }) => string;
  };
  alpha: (color: string, opacity: number) => string;
}

type AnchorProps = {
  'aria-label'?: string;
  ref?: Ref<HTMLElement>;
  onMouseOver?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onTouchStart?: (event: TouchEvent<HTMLElement>) => void;
  onTouchEnd?: (event: TouchEvent<HTMLElement>) => void;
};

type TimerName = 'enter' | 'leave' | 'touch' | 'close' | 'exit';

export interface LightTooltipProps {
  /** The text; an empty one never shows. */
  title: string;
  children: ReactElement<AnchorProps>;
}

/** `:focus-visible`, false where a browser does not know the selector (as MUI's `isFocusVisible`). */
function isFocusVisible(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

export function LightTooltipBase({ title, children, theme }: LightTooltipProps & { theme: LightTooltipTheme }) {
  const id = useId();
  // A sealed widget's floating layer (WidgetSurface): the page's body is outside its styles.
  const { overlays } = useWidgetSurface();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  /** The layer exists while open and while its exit transition runs. */
  const [mounted, setMounted] = useState(false);
  /** Opened by a touch: MUI's larger touch look. */
  const [touch, setTouch] = useState(false);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  /** The layer's translation as last set. */
  const placedRef = useRef({ x: 0, y: 0 });
  const ignoreNonTouchEvents = useRef(false);
  const prevUserSelect = useRef<string | undefined>(undefined);
  const timers = useRef<Partial<Record<TimerName, ReturnType<typeof setTimeout>>>>({});

  const stop = useCallback((name: TimerName) => clearTimeout(timers.current[name]), []);
  const later = useCallback((name: TimerName, ms: number, run: () => void) => {
    clearTimeout(timers.current[name]);
    timers.current[name] = setTimeout(run, ms);
  }, []);

  const { transitions } = theme;
  const growMs = transitions.duration.shorter;

  const openNow = () => {
    clearTimeout(hysteresisTimer);
    hysteresisOpen = true;
    stop('exit');
    setTouch(ignoreNonTouchEvents.current);
    setMounted(true);
    setOpen(true);
  };
  const close = useCallback(() => {
    clearTimeout(hysteresisTimer);
    hysteresisTimer = setTimeout(() => {
      hysteresisOpen = false;
    }, HYSTERESIS_MS);
    setOpen(false);
    later('close', transitions.duration.shortest, () => {
      ignoreNonTouchEvents.current = false;
    });
  }, [later, transitions]);
  const handleEnter = (event: SyntheticEvent) => {
    if (ignoreNonTouchEvents.current && event.type !== 'touchstart') return;
    stop('leave');
    later('enter', hysteresisOpen ? 0 : ENTER_DELAY_MS, openNow);
  };
  const handleLeave = () => {
    stop('enter');
    later('leave', 0, close);
  };
  const stopTouchInteraction = useCallback(() => {
    if (prevUserSelect.current !== undefined) {
      setBodyUserSelect(prevUserSelect.current);
      prevUserSelect.current = undefined;
    }
    stop('touch');
  }, [stop]);

  // Unmounted: no timer fires, and a held touch gives the page its text selection back.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of Object.values(pending)) clearTimeout(timer);
      stopTouchInteraction();
    };
  }, [stopTouchInteraction]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  // Where the layer goes: after every render (a new title changes its width) and whenever the page scrolls or resizes.
  const place = useCallback(() => {
    const layer = layerRef.current;
    const tip = tipRef.current;
    if (!anchor || !layer || !tip) return;
    const gap = touch ? 24 : 14;
    // The margins first: they are part of the box Popper places, below or above alike.
    const setMargins = (above: boolean) => {
      tip.style.margin = above ? `2px 2px ${gap}px` : `${gap}px 2px 2px`;
      tip.style.transformOrigin = above ? 'center bottom' : 'center top';
    };
    setMargins(false);
    const rect = layer.getBoundingClientRect();
    // Where the layer's containing block puts its (0, 0) on screen: the page's top-left (it scrolls with the page),
    // or a positioned or transformed body's.
    const current = layer.style.transform === '' ? { x: 0, y: 0 } : placedRef.current;
    const viewport = window.visualViewport;
    const placed = placeLightTooltip({
      anchor: anchor.getBoundingClientRect(),
      layer: rect,
      view: viewport
        ? { left: viewport.offsetLeft, top: viewport.offsetTop, width: viewport.width, height: viewport.height }
        : { left: 0, top: 0, width: document.documentElement.clientWidth, height: document.documentElement.clientHeight },
    });
    setMargins(placed.above);
    const dpr = window.devicePixelRatio || 1;
    const x = toDevicePixels(placed.x - (rect.left - current.x), dpr);
    const y = toDevicePixels(placed.y - (rect.top - current.y), dpr);
    placedRef.current = { x, y };
    // Popper's own choice: a 3D translation on a high-density screen.
    layer.style.transform = dpr <= 1 ? `translate(${x}px, ${y}px)` : `translate3d(${x}px, ${y}px, 0)`;
  }, [anchor, touch]);
  useLayoutEffect(() => {
    if (mounted) place();
  });
  useEffect(() => {
    if (!mounted) return undefined;
    // A scroll does not leave the shadow root it happens in (a sealed widget's dialog scrolling its content): listen on
    // each root the anchor is in, besides the window.
    const scrollTargets: (Window | ShadowRoot)[] = [window];
    for (let root = anchor?.getRootNode(); isShadowRoot(root); root = root.host.getRootNode()) scrollTargets.push(root);
    for (const target of scrollTargets) target.addEventListener('scroll', place, { capture: true, passive: true });
    window.addEventListener('resize', place);
    return () => {
      for (const target of scrollTargets) target.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
    };
  }, [mounted, place, anchor]);

  // MUI Grow: in, from scale(0.75, 0.5625) and transparent (the opacity over the whole time, the scale over two
  // thirds); out, the same way back with the scale starting a third in; the layer leaves when the time is up.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!tip) return;
    if (open) {
      if (tip.style.transition === '') {
        tip.style.opacity = '0';
        tip.style.transform = GROW_FROM;
      }
      void tip.scrollTop; // a reflow, so the transition starts from there
      tip.style.transition = [
        transitions.create('opacity', { duration: growMs }),
        transitions.create('transform', { duration: growMs * 0.666 }),
      ].join(',');
      tip.style.opacity = '1';
      tip.style.transform = 'none';
    } else {
      tip.style.transition = [
        transitions.create('opacity', { duration: growMs }),
        transitions.create('transform', { duration: growMs * 0.666, delay: growMs * 0.333 }),
      ].join(',');
      tip.style.opacity = '0';
      tip.style.transform = GROW_FROM;
      later('exit', growMs, () => setMounted(false));
    }
  }, [open, mounted, transitions, growMs, later]);

  const childProps = children.props;
  const childRef = childProps.ref;
  const anchorRef = useCallback(
    (node: HTMLElement | null) => {
      setAnchor(node);
      setRef(childRef, node);
    },
    [childRef],
  );
  const anchorProps: AnchorProps = {
    'aria-label': 'aria-label' in childProps ? childProps['aria-label'] : title,
    ref: anchorRef,
    onMouseOver: (event) => {
      childProps.onMouseOver?.(event);
      handleEnter(event);
    },
    onMouseLeave: (event) => {
      childProps.onMouseLeave?.(event);
      handleLeave();
    },
    onFocus: (event) => {
      childProps.onFocus?.(event);
      if (isFocusVisible(event.target)) handleEnter(event);
    },
    onBlur: (event) => {
      childProps.onBlur?.(event);
      if (!isFocusVisible(event.target)) handleLeave();
    },
    onTouchStart: (event) => {
      ignoreNonTouchEvents.current = true;
      childProps.onTouchStart?.(event);
      stop('leave');
      stop('close');
      stopTouchInteraction();
      prevUserSelect.current = bodyUserSelect();
      setBodyUserSelect('none');
      later('touch', ENTER_TOUCH_DELAY_MS, () => {
        setBodyUserSelect(prevUserSelect.current ?? '');
        handleEnter(event);
      });
    },
    onTouchEnd: (event) => {
      childProps.onTouchEnd?.(event);
      stopTouchInteraction();
      later('leave', LEAVE_TOUCH_DELAY_MS, close);
    },
  };

  return (
    <>
      {cloneElement(children, anchorProps)}
      {mounted && title !== '' && anchor
        ? createPortal(
            <div
              ref={layerRef}
              role="tooltip"
              id={id}
              className="MuiPopper-root"
              onMouseOver={handleEnter}
              onMouseLeave={handleLeave}
              style={{
                // Absolute, as Popper's: a fixed layer gets its own compositing layer, which draws its text
                // differently.
                position: 'absolute',
                top: 0,
                left: 0,
                margin: 0,
                zIndex: theme.zIndex.tooltip,
                pointerEvents: open ? 'auto' : 'none',
              }}
            >
              <div
                ref={tipRef}
                style={{
                  backgroundColor: theme.alpha(theme.palette.grey[700], 0.92),
                  borderRadius: theme.shape.borderRadius,
                  color: theme.palette.common.white,
                  fontFamily: theme.typography.fontFamily,
                  padding: touch ? '8px 16px' : '4px 8px',
                  fontSize: theme.typography.pxToRem(touch ? 14 : 11),
                  maxWidth: 300,
                  wordWrap: 'break-word',
                  fontWeight: touch ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
                  lineHeight: touch ? `${Math.round((16 / 14) * 1e5) / 1e5}em` : undefined,
                }}
              >
                {title}
              </div>
            </div>,
            overlays ?? anchor.ownerDocument.body,
          )
        : null}
    </>
  );
}
